import {  Container, DisplayObject, NineSlicePlane, Sprite } from 'pixi.js';
import { Signal } from 'typed-signals';

import { Shared } from '../App';
import { NineSliceButton } from './buttons/NineSliceButton';
import LocalisedText from './LocalisedText';
import { RadioButton, RadioButtonOpts } from './RadioButton';
import { RadioSelector } from './RadioSelector';

const defaultOpts = {
    bg: 'panel',
    title: 'ShopPanel',
    w: 30,
    h: 30,
    nineSliceOffset: 100,
    radioButtonOptions: [] as RadioButtonOpts[], // leave empty to omit radio buttons
    omitArrows: false,
};

export type ShopPanelOpts = typeof defaultOpts;

/*
* Shop panel is intended to provide the base functionality and buttons for the panel class
* In use, it will require a child to be added, the child will be positioned in the center of the card
*
* To make use of the buttons, connect to the signals exposed from the panel
*/
export class ShopPanel
{
    public view: Sprite;
    public content: Container;
    public bg: NineSlicePlane;
    public radioSelector: RadioSelector | undefined;
    public titleText: LocalisedText;

    public signals: {
        onArrowDown: Signal<() => void>,
        onArrowUp: Signal<() => void>,
        onRadioUpdate: Signal<(val: RadioButton, index: number) => void>,
    };

    protected children: Sprite;
    protected downArrowBtn: NineSliceButton;
    protected upArrowBtn: NineSliceButton;

    constructor(opts: Partial<ShopPanelOpts> = {})
    {
        const o = { ...defaultOpts, ...opts };

        const view = new Sprite();

        view.anchor.set(0.5);

        this.signals = {
            onArrowDown: new Signal<() => void>(),
            onArrowUp: new Signal<() => void>(),
            onRadioUpdate: new Signal<(val: RadioButton, index: number) => void>(),
        };

        this.bg = new NineSlicePlane(Shared.assets.getTexture(o.bg),
            o.nineSliceOffset, o.nineSliceOffset, o.nineSliceOffset, o.nineSliceOffset,
        );

        this.bg.width = o.w;
        this.bg.height = o.h;
        this.bg.x -= this.bg.width / 2;
        this.bg.y -= this.bg.height / 2;

        view.addChild(this.bg);
        this.view = view;

        this.content = new Container();
        this.view.addChild(this.content);

        this.titleText = new LocalisedText(o.title, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 5,
            lineJoin: 'round',
            align: 'center',
            fontSize: 30,
            maxWidth: o.w,
        });

        this.titleText.y -= ((o.h / 2) + (this.titleText.height / 2) + 5);
        this.titleText.anchor.set(0.5);
        this.view.addChild(this.titleText);

        this.children = new Sprite();
        this.children.anchor.set(0.5);
        this.view.addChild(this.children);

        const downArrowBtn = Sprite.from(Shared.assets.getTexture('updown-arrow'));

        this.downArrowBtn = new NineSliceButton({
            icon: downArrowBtn,
            w: 180,
            h: 60,
        });
        this.downArrowBtn.view.y += (o.h / 2) - 50;
        this.downArrowBtn.onPress.connect(() =>
        {
            this.signals.onArrowDown.emit();
            Shared.game.soundSystem.playSfx('slide');
        });
        this.view.addChild(this.downArrowBtn.view);

        if (o.radioButtonOptions.length > 0)
        {
            this.radioSelector = new RadioSelector(
                {
                    multiSelect: false,
                    buttons: o.radioButtonOptions,
                });

            this.view.addChild(this.radioSelector.view);
            this.radioSelector.view.y -= (o.h / 2) - (3 * this.radioSelector.height / 4);
            this.radioSelector.signals.valueChanged.connect((val, index) => this.signals.onRadioUpdate.emit(val, index));
        }

        const upArrowBtn = Sprite.from(Shared.assets.getTexture('updown-arrow'));

        upArrowBtn.rotation = Math.PI;
        this.upArrowBtn = new NineSliceButton({
            icon: upArrowBtn,
            w: 180,
            h: 60,
        });
        this.upArrowBtn.onPress.connect(() =>
        {
            this.signals.onArrowUp.emit();
            Shared.game.soundSystem.playSfx('slide');
        });

        if (this.radioSelector)
        {
            this.upArrowBtn.view.y = this.radioSelector.view.y + (this.radioSelector.height / 2) + 50;
        }
        else
        {
            this.upArrowBtn.view.y = 50 - (o.h / 2);
        }

        this.view.addChild(this.upArrowBtn.view);

        if (o.omitArrows)
        {
            this.upArrowBtn.view.visible = false;
            this.downArrowBtn.view.visible = false;
        }
    }

    public addChild(child: DisplayObject): void
    {
        this.children.addChild(child);
    }
}
